import React, { useEffect, useState } from "react";
import apiClient from "../../lib/api";
import { useI18n } from "../../i18n";
import { useSettings } from "../../contexts/SettingsContext";
import { toast } from "sonner";
import { Save, KeyRound, Mail, Globe } from "lucide-react";

const AdminSettings = () => {
  const { lang } = useI18n();
  const { refresh } = useSettings();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { apiClient.get("/admin/settings").then((r) => setS(r.data)); }, []);

  const upd = (k, v) => setS({ ...s, [k]: v });
  const updEmail = (k, v) => setS({ ...s, email: { ...(s.email || {}), [k]: v } });
  const updMollie = (k, v) => setS({ ...s, mollie: { ...(s.mollie || {}), [k]: v } });

  const save = async () => {
    setBusy(true);
    try {
      const { homepage, shipping, seo, navigation, footer, id, ...payload } = s;
      payload.vat_rate = parseFloat(payload.vat_rate);
      await apiClient.put("/admin/settings", payload);
      toast.success(lang === "nl" ? "Opgeslagen" : "Saved");
      refresh();
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  if (!s) return <div className="text-ink-muted">Loading…</div>;
  const email = s.email || {};
  const mollie = s.mollie || {};

  return (
    <div data-testid="admin-settings-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light">{lang === "nl" ? "Instellingen" : "Settings"}</h1>
        <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="settings-save"><Save className="w-4 h-4" /> {busy ? "…" : (lang === "nl" ? "Opslaan" : "Save")}</button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-black/10 bg-white p-6 space-y-3">
          <div className="font-label text-ink-muted">{lang === "nl" ? "Bedrijfsgegevens" : "Company details"}</div>
          <F label="Company name" value={s.company_name} onChange={(v) => upd("company_name", v)} testid="settings-company-name" />
          <F label="KVK number" value={s.kvk_number} onChange={(v) => upd("kvk_number", v)} testid="settings-kvk" />
          <F label="BTW (VAT) number" value={s.btw_number} onChange={(v) => upd("btw_number", v)} testid="settings-btw" />
          <F label="Address line 1" value={s.address_line1} onChange={(v) => upd("address_line1", v)} testid="settings-address1" />
          <F label="Address line 2" value={s.address_line2} onChange={(v) => upd("address_line2", v)} testid="settings-address2" />
        </div>
        <div className="border border-black/10 bg-white p-6 space-y-3">
          <div className="font-label text-ink-muted">{lang === "nl" ? "Contact & belasting" : "Contact & tax"}</div>
          <F label="Support email" type="email" value={s.support_email} onChange={(v) => upd("support_email", v)} testid="settings-support-email" />
          <F label="Phone" value={s.phone} onChange={(v) => upd("phone", v)} testid="settings-phone" />
          <label className="block">
            <div className="text-xs font-label text-ink-muted mb-1">{lang === "nl" ? "BTW-tarief (%)" : "VAT rate (%)"}</div>
            <select value={s.vat_rate} onChange={(e) => upd("vat_rate", parseFloat(e.target.value))} className="border border-black/15 bg-white px-3 py-2 text-sm w-full" data-testid="settings-vat-rate">
              <option value={21}>21% — {lang === "nl" ? "Algemeen" : "Standard"}</option>
              <option value={9}>9% — {lang === "nl" ? "Verlaagd" : "Reduced"}</option>
              <option value={0}>0% — {lang === "nl" ? "Vrijgesteld / Export" : "Zero / Export"}</option>
            </select>
          </label>
          <F label="Currency" value={s.currency} onChange={(v) => upd("currency", v)} testid="settings-currency" />
        </div>
      </div>

      <div className="border border-black/10 bg-white p-6 space-y-3 mt-6">
        <div className="font-label text-ink-muted flex items-center gap-2"><Mail className="w-4 h-4" /> {lang === "nl" ? "Email (Resend)" : "Email (Resend)"}</div>
        <p className="text-xs text-ink-muted">{lang === "nl" ? "Genereer een API key op resend.com → API Keys. Verifieer je domein (snapshop.it.com) en gebruik dan orders@snapshop.it.com als afzender." : "Generate an API key at resend.com → API Keys. Verify your domain (snapshop.it.com) then use orders@snapshop.it.com as sender."}</p>
        <div className="grid md:grid-cols-3 gap-3">
          <F label="Resend API key" type="password" value={email.resend_api_key} onChange={(v) => updEmail("resend_api_key", v)} testid="settings-resend-key" placeholder="re_..." icon={KeyRound} />
          <F label="From email" type="email" value={email.from_email} onChange={(v) => updEmail("from_email", v)} testid="settings-from-email" placeholder="orders@snapshop.it.com" />
          <F label="Admin notify email" type="email" value={email.admin_notify_email} onChange={(v) => updEmail("admin_notify_email", v)} testid="settings-admin-notify" placeholder="admin@snapshop.it.com" />
        </div>
        <p className="text-xs text-terracotta">{!email.resend_api_key ? (lang === "nl" ? "Geen API key — emails worden in de wachtrij geplaatst." : "No API key — emails will be queued.") : (lang === "nl" ? "Resend is actief." : "Resend is active.")}</p>
      </div>

      <div className="border border-black/10 bg-white p-6 space-y-3 mt-6">
        <div className="font-label text-ink-muted flex items-center gap-2"><KeyRound className="w-4 h-4" /> {lang === "nl" ? "Mollie (iDEAL betalingen)" : "Mollie (iDEAL payments)"}</div>
        <p className="text-xs text-ink-muted">{lang === "nl" ? "Genereer een API key op https://my.mollie.com/dashboard/developers/api-keys. Begin met je test_xxx key. Schakel pas over naar live_xxx zodra je Mollie-account is geverifieerd." : "Generate an API key at https://my.mollie.com/dashboard/developers/api-keys. Start with your test_xxx key. Only switch to live_xxx once your Mollie account is verified."}</p>
        <div className="grid md:grid-cols-3 gap-3">
          <F label={lang === "nl" ? "Modus" : "Mode"} testid="settings-mollie-mode" type="select" value={mollie.mode || "test"} onChange={(v) => updMollie("mode", v)} options={[{ v: "test", l: "Test" }, { v: "live", l: "Live" }]} />
          <F label="Test API key (test_...)" type="password" value={mollie.test_api_key} onChange={(v) => updMollie("test_api_key", v)} testid="settings-mollie-test-key" placeholder="test_xxxxxxxx" icon={KeyRound} />
          <F label="Live API key (live_...)" type="password" value={mollie.live_api_key} onChange={(v) => updMollie("live_api_key", v)} testid="settings-mollie-live-key" placeholder="live_xxxxxxxx" icon={KeyRound} />
        </div>
        <p className="text-xs">
          {(() => {
            const mode = mollie.mode || "test";
            const key = mode === "live" ? mollie.live_api_key : mollie.test_api_key;
            if (!key) return <span className="text-terracotta">{lang === "nl" ? "iDEAL is uitgeschakeld — vul de bijbehorende API key in." : "iDEAL is disabled — fill in the API key for the active mode."}</span>;
            return <span className="text-olive">{lang === "nl" ? `iDEAL via Mollie is actief (${mode} mode).` : `iDEAL via Mollie is active (${mode} mode).`}</span>;
          })()}
        </p>
      </div>

      <div className="border border-black/10 bg-white p-6 space-y-3 mt-6">
        <div className="font-label text-ink-muted flex items-center gap-2"><Globe className="w-4 h-4" /> {lang === "nl" ? "Domein" : "Domain"}</div>
        <F label="Site domain (used in emails, sitemap, SEO)" value={s.site_domain} onChange={(v) => upd("site_domain", v)} testid="settings-site-domain" placeholder="snapshop.it.com" />
        <p className="text-xs text-ink-muted">{lang === "nl" ? "Custom domein koppelen: ga naar je Emergent deploy panel → Domains → voeg snapshop.it.com toe en wijs de DNS A/CNAME records naar de Emergent endpoints." : "To connect a custom domain: open your Emergent deploy panel → Domains → add snapshop.it.com and point DNS A/CNAME records to the Emergent endpoints."}</p>
      </div>
    </div>
  );
};

const F = ({ label, value, onChange, testid, type = "text", placeholder, icon: Icon, options }) => (
  <label className="block">
    <div className="text-xs font-label text-ink-muted mb-1 flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5" />} {label}</div>
    {type === "select" ? (
      <select data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="border border-black/15 px-3 py-2 text-sm w-full bg-white">
        {(options || []).map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    ) : (
      <input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="border border-black/15 px-3 py-2 text-sm w-full bg-white" />
    )}
  </label>
);

export default AdminSettings;
